import { DEPLOYMENT_ENVIRONMENTS, type DeploymentEnvironmentNames } from '../common/constants/ppConstants.ts'; // `.ts` suffix ist hier nötig, weil das File über `server.ts` auch direkt von Node importiert wird
import { formatStructuredLogLine } from '../../server/utils/structuredLogLine.ts';

// Provider für server-seitige Werte (gesetzt beim Server-Start)
let serverTraceIdProvider: (() => string | undefined | null) | undefined;
let serverEnvProvider: (() => DeploymentEnvironmentNames | undefined) | undefined;

export function setServerProviders(
  traceIdFn: () => string | undefined | null,
  envFn: () => DeploymentEnvironmentNames | undefined
) {
  serverTraceIdProvider = traceIdFn;
  serverEnvProvider = envFn;
}

export type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
export type ConsoleLogArgs = {
  loggerName?: string;
  logLevel: LogLevel;
  env?: DeploymentEnvironmentNames;
  tgTraceId?: string | null;
  message: string;
  args?: any[];
  fontSpec?: string;
};

function resolveEnv(): DeploymentEnvironmentNames | undefined {
  if (typeof window !== 'undefined') {
    return window.TG_CLIENT_CONFIG.deploymentInfo.DEPLOYMENT_ENVIRONMENT as DeploymentEnvironmentNames;
  }
  return serverEnvProvider?.();
}

function readTraceIdFromCookie() {
  try {
    const raw = document.cookie
      .split('; ')
      .find(c => c.startsWith('tg_trace-id='))
      ?.split('=')[1];
    if (raw) {
      return JSON.parse(atob(decodeURIComponent(raw)));
    }
  } catch {
    // ignore malformed cookie
  }
  return undefined;
}

function resolveTraceId(): string | undefined | null {
  if (typeof window !== 'undefined') {
    if (!window.__tgTraceIdCached) {
      window.__tgTraceIdCached = readTraceIdFromCookie();
    }
    return window.__tgTraceIdCached;
  }
  return serverTraceIdProvider?.();
}

function logToDevConsole(msg: string, logLevel: LogLevel, fontSpec?: string) {
  if (fontSpec) {
    console.log(`%c${msg}`, fontSpec);
    return;
  }
  const colorByLevel: Record<string, string> = {
    ERROR: 'color: red; font-weight: bold;',
    WARN: 'color: orange; font-weight: bold;',
    INFO: 'color: dodgerblue',
    DEBUG: 'color: gray'
  };
  const color = colorByLevel[logLevel] || '';
  console.log(`%c${msg}`, color);
}

export function logToConsole({
  loggerName = 'hub-web-frontend',
  logLevel = 'DEBUG',
  env,
  tgTraceId,
  message,
  args = [],
  fontSpec
}: ConsoleLogArgs) {
  if (!env) {
    env = resolveEnv();
  }

  const logMessage: any = {
    logger: loggerName,
    'log.level': logLevel
  };

  if (!tgTraceId) {
    tgTraceId = resolveTraceId();
  }
  if (tgTraceId) {
    logMessage.tgTraceId = tgTraceId;
  }

  // Wir dürfen kein `message`-Feld auf der logMeassage haben, weil die Logstash Pipeline von Transgourmet sonst nur
  // dieses Feld weiterverwendet und nicht die gesamte logMessage.
  // => daher verwendenn wir `msg` statt `message`.
  logMessage.msg = message + (args.length > 0 ? `\n> [ ${args.map(a => serialize(a)).join(',')} ]` : '');

  if (typeof window !== 'undefined' || env === DEPLOYMENT_ENVIRONMENTS.DEVELOPMENT) {
    logToDevConsole(logMessage.msg, logLevel, fontSpec);
  } else {
    console.log(formatStructuredLogLine(logMessage));
  }
}

// Custom serialization utility
// Primär um Escape-Characters zu verhindern, wenn ein JSON-formatierter String nochmals mit JSON.stringify serialisiert wird.
export function serialize(obj, seen: WeakSet<object> = new WeakSet()) {
  if (typeof obj !== 'object' || obj === null) {
    return String(obj);
  }
  if (seen.has(obj)) {
    return '[Circular]';
  }
  seen.add(obj);

  if (obj instanceof Error) {
    const errors = collectErrors(obj);
    return errors.map(e => e.message).join(', ');
  }

  return Object.entries(obj)
    .map(([key, value]) => {
      if (value && typeof value === 'object') {
        return `${key}={${serialize(value, seen)}}`;
      } else {
        return `${key}=${value}`;
      }
    })
    .join(', ');
}

function collectErrors(err: unknown, seen = new Set<unknown>()): Error[] {
  const result: Error[] = [];

  function visit(e: unknown) {
    if (!e || typeof e !== 'object') return;
    if (seen.has(e)) return; // verhindert Zyklen
    seen.add(e);

    if (e instanceof Error) {
      result.push(e);

      const cause = (e as Error & { cause?: unknown }).cause;
      if (cause) {
        visit(cause);
      }
    }
  }

  visit(err);
  return result;
}
